import { useState, useEffect } from 'react';
import { useNavigate, useParams } from 'react-router-dom';
import api from '../../utils/api';
import { toast } from 'react-toastify';

const CreateProject = () => {
  const navigate = useNavigate();
  const { id } = useParams();
  const isEdit = Boolean(id);
  const [employees, setEmployees] = useState([]);
  const [form, setForm] = useState({
    name: '', key: '', description: '', status: 'Active', lead: '', members: [],
  });
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    const fetchData = async () => {
      try {
        const empRes = await api.get('/employees', { params: { limit: 200 } });
        setEmployees(empRes.data.employees || []);

        if (isEdit) {
          const projRes = await api.get(`/projects/${id}`);
          const p = projRes.data;
          setForm({
            name: p.name,
            key: p.key,
            description: p.description || '',
            status: p.status,
            lead: p.lead?._id || '',
            members: p.members?.map(m => m._id) || [],
          });
        }
      } catch (err) {
        toast.error('Failed to load data');
      } finally {
        setLoading(false);
      }
    };
    fetchData();
  }, [id, isEdit]);

  const handleNameChange = (e) => {
    const name = e.target.value;
    setForm(prev => ({
      ...prev,
      name,
      key: isEdit ? prev.key : name.replace(/[^a-zA-Z]/g, '').toUpperCase().slice(0, 6),
    }));
  };

  const toggleMember = (empId) => {
    setForm(prev => ({
      ...prev,
      members: prev.members.includes(empId)
        ? prev.members.filter(m => m !== empId)
        : [...prev.members, empId],
    }));
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    try {
      if (isEdit) {
        await api.put(`/projects/${id}`, form);
        toast.success('Project updated');
      } else {
        await api.post('/projects', form);
        toast.success('Project created');
      }
      navigate('/projects');
    } catch (err) {
      toast.error(err.response?.data?.message || 'Operation failed');
    }
  };

  if (loading) return <div className="loading">Loading...</div>;

  return (
    <div>
      <div className="page-header">
        <h1>{isEdit ? 'Edit' : 'Create'} Project</h1>
      </div>

      <div className="form-card">
        <form onSubmit={handleSubmit}>
          <div className="form-grid">
            <div className="form-group">
              <label>Project Name *</label>
              <input value={form.name} onChange={handleNameChange} required />
            </div>
            <div className="form-group">
              <label>Project Key *</label>
              <input
                value={form.key}
                onChange={e => setForm({ ...form, key: e.target.value.toUpperCase().replace(/[^A-Z]/g, '').slice(0, 6) })}
                required
                disabled={isEdit}
                placeholder="e.g. HRMS"
                maxLength={6}
              />
            </div>
            <div className="form-group">
              <label>Project Lead</label>
              <select value={form.lead} onChange={e => setForm({ ...form, lead: e.target.value })}>
                <option value="">Select Lead</option>
                {employees.map(emp => (
                  <option key={emp._id} value={emp._id}>{emp.name} ({emp.employeeId})</option>
                ))}
              </select>
            </div>
            <div className="form-group">
              <label>Status</label>
              <select value={form.status} onChange={e => setForm({ ...form, status: e.target.value })}>
                <option value="Active">Active</option>
                <option value="Archived">Archived</option>
              </select>
            </div>
            <div className="form-group" style={{ gridColumn: '1 / -1' }}>
              <label>Description</label>
              <textarea
                value={form.description}
                onChange={e => setForm({ ...form, description: e.target.value })}
                rows={3}
                placeholder="Project description..."
              />
            </div>
            <div className="form-group" style={{ gridColumn: '1 / -1' }}>
              <label>Team Members</label>
              <div className="member-checkboxes">
                {employees.map(emp => (
                  <label key={emp._id} className="member-checkbox">
                    <input
                      type="checkbox"
                      checked={form.members.includes(emp._id)}
                      onChange={() => toggleMember(emp._id)}
                    />
                    <span>{emp.name}</span>
                  </label>
                ))}
              </div>
            </div>
          </div>

          <div style={{ display: 'flex', gap: '1rem', marginTop: '1.5rem' }}>
            <button type="submit" className="btn-primary">{isEdit ? 'Update' : 'Create'} Project</button>
            <button type="button" className="btn-secondary" onClick={() => navigate('/projects')}>Cancel</button>
          </div>
        </form>
      </div>
    </div>
  );
};

export default CreateProject;
